import { useCallback, useEffect, useMemo } from 'react';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { refreshSessionInit } from '@/store/sessionSlice';

/** Stores for active company — loaded via session init. Refreshes when company scope changes. */
export function useActiveStores() {
  const dispatch = useAppDispatch();
  const companyScopeVersion = useAppSelector((s) => s.app.companyScopeVersion);
  const selectedCompanyId = useAppSelector((s) => s.app.selectedCompany?.id);
  const stores = useAppSelector((s) => s.session.stores);
  const sessionCompanyId = useAppSelector((s) => s.session.activeCompanyId);
  const loading = useAppSelector((s) => s.session.loading);
  const loaded = useAppSelector((s) => s.session.loaded);

  const scopedStores = useMemo(() => {
    if (!selectedCompanyId) return stores;
    return stores.filter((s) => s.companyId == null || s.companyId === selectedCompanyId);
  }, [stores, selectedCompanyId]);

  const reload = useCallback(async () => {
    await dispatch(refreshSessionInit(selectedCompanyId));
  }, [dispatch, selectedCompanyId]);

  useEffect(() => {
    if (!loaded || loading || selectedCompanyId == null || sessionCompanyId == null) return;
    if (selectedCompanyId !== sessionCompanyId) {
      void reload();
    }
  }, [loaded, loading, selectedCompanyId, sessionCompanyId, reload, companyScopeVersion]);

  return {
    stores: scopedStores,
    loading: loading || (!loaded && scopedStores.length === 0),
    reload,
    companyScopeVersion,
  };
}
